import { Command } from 'commander';
import chalk from 'chalk';
import { existsSync, mkdirSync, writeFileSync } from 'fs';
import { dirname, join } from 'path';
import { execSync } from 'child_process';
import { OutputFormatter, getJsonFlag } from '../lib/output.js';
import { SKILL_CONTENT, SKILL_FILES } from '../generated/skill-content.js';

// Re-export so tests and external callers can still import SKILL_CONTENT
// from this module (backward compat with pre-R4 imports).
export { SKILL_CONTENT };

/**
 * Find the git repository root directory.
 * Falls back to process.cwd() if not inside a git repo.
 */
export function findRepoRoot(): string {
  try {
    return execSync('git rev-parse --show-toplevel', {
      encoding: 'utf-8',
      stdio: ['pipe', 'pipe', 'pipe'],
    }).trim();
  } catch {
    return process.cwd();
  }
}

// =============================================================================
// Skill Content
// =============================================================================
//
// The canonical source of truth for the skill content is a real markdown
// file living in the CLI package's own Claude Code workspace:
//
//     mutagent-cli/.claude/skills/mutagent-cli/SKILL.md
//
// A prebuild/pretest/prelint/pretype-check hook (`scripts/sync-skill.ts`)
// reads that file and regenerates `src/generated/skill-content.ts`,
// exporting the raw markdown as `SKILL_CONTENT`. Keeping the source as
// a real .md file (not a hardcoded TypeScript string) makes it
// maintainable; the generated TS module is a build implementation detail.
//
// TWO `.claude/` dirs, at TWO different levels:
//   - SOURCE: `mutagent-cli/.claude/skills/mutagent-cli/SKILL.md`
//     (this package's own Claude Code workspace — package-local)
//   - DESTINATION: `<user-cwd>/.claude/skills/mutagent-cli/SKILL.md`
//     (the end user's top-level project .claude/, where `skills install`
//     WRITES on the user's machine — SKILL_DIR below)
// Same relative path, different levels, never collide.
//
// If `SKILL_CONTENT` is missing or empty at runtime, the command throws
// LOUDLY — there is no fallback, by design. The skill dump pipeline
// either ships the real content or fails the build.

const SKILL_DIR = '.claude/skills/mutagent-cli';
const SKILL_ENTRY_FILE = 'SKILL.md';

function assertSkillFilesValid(): void {
  if (Object.keys(SKILL_FILES).length === 0) {
    throw new Error(
      'SKILL_FILES is missing or empty. This indicates a broken build: ' +
        'scripts/sync-skill.ts must regenerate src/generated/skill-content.ts ' +
        'from .claude/skills/mutagent-cli/ before build/test. ' +
        'Run `bun run sync-skill` to debug.',
    );
  }
  const entry = SKILL_FILES[SKILL_ENTRY_FILE];
  if (typeof entry !== 'string' || entry.trim().length === 0) {
    throw new Error(
      `SKILL_FILES["${SKILL_ENTRY_FILE}"] is missing or empty. ` +
        'Check .claude/skills/mutagent-cli/SKILL.md and run `bun run sync-skill`.',
    );
  }
  if (!entry.startsWith('---')) {
    throw new Error(
      `SKILL_FILES["${SKILL_ENTRY_FILE}"] is malformed: expected YAML frontmatter ("---") at start.`,
    );
  }
}

/**
 * Returns the canonical skill entry content (SKILL.md). Exported for tests
 * and back-compat with pre-R4 callers. New code should use
 * `getSkillFiles()` to walk the full tree including workflows/ and concepts/.
 */
export function getSkillContent(): string {
  assertSkillFilesValid();
  return SKILL_FILES[SKILL_ENTRY_FILE] ?? '';
}

/**
 * Returns the full skill file tree as a map keyed by path relative to the
 * skill root (e.g. `SKILL.md`, `workflows/prompts.md`,
 * `concepts/eval-criteria.md`). Exported for tests so the pipeline can be
 * asserted from outside the command action.
 */
export function getSkillFiles(): Record<string, string> {
  assertSkillFilesValid();
  return SKILL_FILES;
}

// =============================================================================
// Skills Command
// =============================================================================

export function createSkillsCommand(): Command {
  const skills = new Command('skills').description(
    'Manage MutagenT CLI skills for coding agents',
  );

  skills
    .command('install')
    .description('Install MutagenT CLI skill for Claude Code')
    .addHelpText(
      'after',
      `
Examples:
  ${chalk.dim('$')} mutagent skills install

This creates a Claude Code skill at .claude/skills/mutagent-cli/SKILL.md
that teaches coding agents how to use the MutagenT CLI effectively.
    `,
    )
    .action((_options: Record<string, unknown>, cmd: Command) => {
      const parentCmd = cmd.parent?.parent;
      const isJson = parentCmd ? getJsonFlag(parentCmd) : false;
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      const repoRoot = findRepoRoot();
      const skillDir = join(repoRoot, SKILL_DIR);

      // Write the full skill tree — source of truth is the package-local
      // mutagent-cli/.claude/skills/mutagent-cli/**/*.md, synced into
      // src/generated/skill-content.ts at build time.
      const files = getSkillFiles();
      const writtenFiles: { path: string; bytes: number }[] = [];
      let totalBytes = 0;

      // Stable sorted traversal — entry file first for tree rendering.
      const sortedKeys = Object.keys(files).sort((a, b) => {
        if (a === SKILL_ENTRY_FILE) return -1;
        if (b === SKILL_ENTRY_FILE) return 1;
        return a.localeCompare(b);
      });

      for (const relPath of sortedKeys) {
        const destPath = join(skillDir, relPath);
        const parentDir = dirname(destPath);
        if (!existsSync(parentDir)) {
          mkdirSync(parentDir, { recursive: true });
        }
        const raw = files[relPath] ?? '';
        const finalContent = raw.endsWith('\n') ? raw : `${raw}\n`;
        writeFileSync(destPath, finalContent, 'utf-8');
        writtenFiles.push({ path: destPath, bytes: finalContent.length });
        totalBytes += finalContent.length;
      }

      if (isJson) {
        output.output({
          installed: true,
          name: 'mutagent-cli',
          skillDir,
          files: writtenFiles,
          totalBytes,
        });
      } else {
        output.success(`Installed MutagenT CLI skill`);
        console.log(`  ${chalk.dim('Dir:')} ${skillDir}`);
        console.log('');
        // Render a simple tree of written files
        for (let i = 0; i < writtenFiles.length; i++) {
          const entry = writtenFiles[i];
          if (!entry) continue;
          const relPath = sortedKeys[i] ?? '';
          const isLast = i === writtenFiles.length - 1;
          const prefix = isLast ? '└─' : '├─';
          console.log(
            `  ${chalk.dim(prefix)} ${relPath} ${chalk.dim(`(${String(entry.bytes)} bytes)`)}`,
          );
        }
        console.log('');
        console.log(
          `  ${chalk.dim(`${String(sortedKeys.length)} file(s), ${String(totalBytes)} bytes total.`)}`,
        );
        console.log(
          `  ${chalk.dim('This skill teaches coding agents how to use the MutagenT CLI.')}`,
        );
        console.log(
          `  ${chalk.dim('It will be automatically loaded by Claude Code when relevant triggers match.')}`,
        );
      }
    });

  return skills;
}
